import React, { useEffect } from 'react';
import { StanModal } from '@/components';
import { useMFAConfiguration } from '../../../hooks/useMFAConfiguration';
import { MFA_FLOW_STAGES, MFAConfigSource } from '../../../constants/mfa';
import { MFAMethodChoice } from '../mfa/MFAMethodChoice';
import { MFAAuthenticatorSetup } from '../mfa/MFAAuthenticatorSetup';
import { MFARecoveryCodes } from '../mfa/MFARecoveryCodes';
import { MFANameMethod } from '../mfa/MFANameMethod';
import { MFAComplete } from '../mfa/MFAComplete';
import { copyToClipboard, downloadFile } from '@/lib/helpers';

interface ConfigureMFAModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    source?: MFAConfigSource;
}

export const ConfigureMFAModal: React.FC<ConfigureMFAModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    source = 'toggle',
}) => {
    const mfa = useMFAConfiguration({
        source,
        onSuccess,
        onClose,
    });

    // Destructure reset to avoid dependency issues
    const { reset } = mfa;

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const handleClose = () => {
        mfa.reset();
        onClose();
    };

    const handleCopyRecoveryCode = () => {
        copyToClipboard(mfa.recoveryCode, {
            successMessage: 'Backup codes copied!',
            showNotifications: true,
        });
        mfa.setRecoverySaveAction('copied');
    };

    const handleDownloadRecoveryCode = () => {
        // Create blob and download
        const blob = new Blob([mfa.recoveryCode], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        downloadFile(url, 'stan-recovery-codes.txt');
        URL.revokeObjectURL(url);
        mfa.setRecoverySaveAction('downloaded');
    };

    const renderContent = () => {
        switch (mfa.flowStage) {
            case MFA_FLOW_STAGES.METHOD_CHOICE:
                return (
                    <MFAMethodChoice
                        source={source}
                        onSelectAuthenticator={mfa.goToAuthenticatorSetup}
                        isLoading={mfa.isLoading}
                    />
                );

            case MFA_FLOW_STAGES.AUTHENTICATOR_APP:
                return (
                    <MFAAuthenticatorSetup
                        source={source}
                        totpURI={mfa.totpURI}
                        secret={mfa.secret}
                        showSetupKey={mfa.showSetupKey}
                        onToggleView={mfa.toggleSetupKeyView}
                        onVerifyCode={mfa.verifyOTP}
                        isLoading={mfa.isLoading}
                        error={mfa.error}
                        onClearError={mfa.clearError}
                    />
                );

            case MFA_FLOW_STAGES.SAVE_RECOVERY_CODES:
                return (
                    <MFARecoveryCodes
                        source={source}
                        recoveryCode={mfa.recoveryCode}
                        recoverySaveAction={mfa.recoverySaveAction}
                        onCopy={handleCopyRecoveryCode}
                        onDownload={handleDownloadRecoveryCode}
                        onContinue={mfa.goToNameConfiguration}
                    />
                );

            case MFA_FLOW_STAGES.NAME_CONFIGURATION:
                return (
                    <MFANameMethod
                        source={source}
                        authenticatorName={mfa.authenticatorName}
                        onNameChange={mfa.setAuthenticatorName}
                        onFinishSetup={mfa.enableMFAWithName}
                        isLoading={mfa.isLoading}
                    />
                );

            case MFA_FLOW_STAGES.COMPLETE:
                return <MFAComplete source={source} onComplete={mfa.completeFlow} />;

            default:
                return null;
        }
    };

    return (
        <StanModal id='configure-mfa-modal' isOpen={isOpen} onClose={handleClose} size='sm'>
            {renderContent()}
        </StanModal>
    );
};
